import React from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client";

import { UserContext } from "../../../../platform/components/auth/auth-context";
import SmsConsentCheckbox, {
  SMS_CONSENT_TEXT,
} from "../../../../platform/components/legal/SmsConsentCheckbox";
import { SEND_EMAIL_MUTATION } from "../../../../platform/graphql/mutations/business";
import { GET_ALL_COUNTRIES } from "../../../../platform/graphql/queries/countryStateCity";

const SUPPORT_EMAIL = "support@plany365.com";
const NO_REPLY_EMAIL = "no-reply@plany365.com";
const DEFAULT_COUNTRY_ID = 228;

const JOB_ALERT_ROLE_OPTIONS = [
  "Caregiver",
  "CNA",
  "HCA",
  "Med Tech",
  "RN",
  "LPN",
  "Live-in Care",
  "Overnight Care",
] as const;

const JOB_ALERT_EMPLOYMENT_TYPE_OPTIONS = [
  "Full-time",
  "Part-time",
  "Per diem",
  "Live-in",
  "Temporary / Contract",
] as const;

type CountryCity = { id: number | string; name: string };
type CountryState = { id: number | string; name: string; cities?: CountryCity[] };
type Country = { id: number | string; name: string; states?: CountryState[] };

type AlertFormState = {
  fullName: string;
  email: string;
  phone: string;
  preferredRoles: string[];
  otherPreferredRole: string;
  preferredCountryId: number | null;
  preferredStateId: number | null;
  preferredCityId: number | null;
  employmentTypes: string[];
  otherEmploymentType: string;
  notes: string;
};

const INITIAL_FORM: AlertFormState = {
  fullName: "",
  email: "",
  phone: "",
  preferredRoles: [],
  otherPreferredRole: "",
  preferredCountryId: null,
  preferredStateId: null,
  preferredCityId: null,
  employmentTypes: [],
  otherEmploymentType: "",
  notes: "",
};

function eqId(a: unknown, b: unknown) {
  return Number(a) === Number(b);
}

function asSelectValue(id?: number | null) {
  return id != null ? String(id) : "";
}

function getSelectedCountry(countries: Country[], countryId: number | null) {
  return countryId != null
    ? countries.find((country) => eqId(country.id, countryId)) ?? null
    : null;
}

function getSelectedState(country: Country | null, stateId: number | null) {
  return stateId != null
    ? country?.states?.find((state) => eqId(state.id, stateId)) ?? null
    : null;
}

function getSelectedCity(state: CountryState | null, cityId: number | null) {
  return cityId != null
    ? state?.cities?.find((city) => eqId(city.id, cityId)) ?? null
    : null;
}

function buildChoiceSummary(selected: string[], otherValue: string) {
  const other = String(otherValue || "").trim();
  const merged = [...selected];
  if (other) merged.push(other);
  return merged.length ? merged.join(", ") : "-";
}

function buildPreferredLocationLabel(form: AlertFormState, countries: Country[]) {
  const country = getSelectedCountry(countries, form.preferredCountryId);
  const state = getSelectedState(country, form.preferredStateId);
  const city = getSelectedCity(state, form.preferredCityId);
  return [city?.name, state?.name, country?.name].filter(Boolean).join(", ") || "-";
}

function buildSupportEmailText(form: AlertFormState, countries: Country[]) {
  return [
    "New care jobs alert signup",
    "",
    `Full name: ${form.fullName.trim() || "-"}`,
    `Email: ${form.email.trim() || "-"}`,
    `Phone: ${form.phone.trim() || "-"}`,
    "SMS consent: Yes",
    `SMS consent text: ${SMS_CONSENT_TEXT}`,
    `Preferred roles: ${buildChoiceSummary(form.preferredRoles, form.otherPreferredRole)}`,
    `Preferred location: ${buildPreferredLocationLabel(form, countries)}`,
    `Employment types: ${buildChoiceSummary(form.employmentTypes, form.otherEmploymentType)}`,
    "",
    "Notes",
    form.notes.trim() || "-",
  ].join("\n");
}

function buildConfirmationEmailText(form: AlertFormState, countries: Country[]) {
  return [
    `Hi ${form.fullName.trim() || "there"},`,
    "",
    "You are signed up for free care job alerts from Plany365.",
    `Preferred roles: ${buildChoiceSummary(form.preferredRoles, form.otherPreferredRole)}`,
    `Preferred location: ${buildPreferredLocationLabel(form, countries)}`,
    `Employment types: ${buildChoiceSummary(form.employmentTypes, form.otherEmploymentType)}`,
    "",
    "You can also search and apply to open jobs for free anytime at:",
    "https://plany365.com/care/jobs",
    "",
    "Thank you,",
    "Plany365 Care Jobs",
  ].join("\n");
}

function toggleArrayValue(values: string[], nextValue: string) {
  return values.includes(nextValue)
    ? values.filter((value) => value !== nextValue)
    : [...values, nextValue];
}

export default function CareJobAlertSignupCard(): JSX.Element {
  const { providerUser, user } = React.useContext(UserContext) as any;
  const [sendEmail] = useMutation(SEND_EMAIL_MUTATION);
  const countriesQuery = useQuery(GET_ALL_COUNTRIES, { fetchPolicy: "cache-first" });
  const [form, setForm] = React.useState<AlertFormState>(INITIAL_FORM);
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [smsConsent, setSmsConsent] = React.useState(false);

  const countries: Country[] = React.useMemo(
    () =>
      Array.isArray(countriesQuery.data?.getAllCountries)
        ? (countriesQuery.data.getAllCountries as Country[])
        : [],
    [countriesQuery.data?.getAllCountries]
  );

  const preferredCountry = React.useMemo(
    () => getSelectedCountry(countries, form.preferredCountryId),
    [countries, form.preferredCountryId]
  );
  const preferredState = React.useMemo(
    () => getSelectedState(preferredCountry, form.preferredStateId),
    [preferredCountry, form.preferredStateId]
  );
  const preferredStates = preferredCountry?.states ?? [];
  const preferredCities = preferredState?.cities ?? [];

  React.useEffect(() => {
    const displayName =
      String(providerUser?.username || user?.username || user?.name || "").trim();
    const email = String(providerUser?.email || user?.email || "").trim();
    const phone = String(providerUser?.phone || user?.phone || "").trim();

    setForm((prev) => ({
      ...prev,
      fullName: prev.fullName || displayName,
      email: prev.email || email,
      phone: prev.phone || phone,
    }));
  }, [providerUser, user]);

  React.useEffect(() => {
    if (!countries.length) return;

    const hasDefaultCountry = countries.some((country) => eqId(country.id, DEFAULT_COUNTRY_ID));
    if (!hasDefaultCountry) return;

    setForm((prev) => {
      if (prev.preferredCountryId != null) return prev;
      return { ...prev, preferredCountryId: DEFAULT_COUNTRY_ID };
    });
  }, [countries]);

  const updateField = React.useCallback(
    (field: keyof AlertFormState, value: string | string[] | number | null) => {
      setForm((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  const handleSubmit = React.useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      setError(null);
      setSuccess(null);

      if (
        !form.fullName.trim() ||
        !form.email.trim() ||
        !form.preferredCountryId ||
        !form.preferredStateId ||
        !form.preferredCityId
      ) {
        setError("Please add your name, email, and preferred location.");
        return;
      }
      if (!smsConsent) {
        setError("Please agree to the SMS consent before signing up for job alerts.");
        return;
      }

      setSubmitting(true);
      try {
        const locationLabel = buildPreferredLocationLabel(form, countries);

        await sendEmail({
          variables: {
            from_email: NO_REPLY_EMAIL,
            to: SUPPORT_EMAIL,
            subject: `[Care Jobs Alert] ${form.fullName.trim()} - ${locationLabel}`,
            text: buildSupportEmailText(form, countries),
          },
        });

        await sendEmail({
          variables: {
            from_email: NO_REPLY_EMAIL,
            to: form.email.trim(),
            subject: "You are signed up for free care job alerts",
            text: buildConfirmationEmailText(form, countries),
          },
        });

        setSuccess("You are signed up for free care job alerts.");
        setForm((prev) => ({
          ...INITIAL_FORM,
          fullName: prev.fullName,
          email: prev.email,
          phone: prev.phone,
          preferredCountryId: prev.preferredCountryId || DEFAULT_COUNTRY_ID,
        }));
        setSmsConsent(false);
      } catch (submitError: any) {
        setError(
          String(
            submitError?.message ||
              "We could not save your job alert request right now. Please try again."
          )
        );
      } finally {
        setSubmitting(false);
      }
    },
    [countries, form, sendEmail, smsConsent]
  );

  return (
    <Paper
      variant="outlined"
      sx={{
        p: { xs: 2, sm: 2.5 },
        borderRadius: 3,
        borderColor: "#dbe3ef",
        background: "linear-gradient(180deg, #fcfeff 0%, #f7fbff 100%)",
      }}
    >
      <Stack spacing={2}>
        <Stack spacing={0.75}>
          <Typography variant="h5" sx={{ fontWeight: 800, color: "#173550" }}>
            Get free care job alerts
          </Typography>
          <Typography variant="body2" sx={{ color: "#48657a" }}>
            Sign up once and we will send you care-related openings. You can still
            search and apply for free any time.
          </Typography>
        </Stack>

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Stack spacing={2}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Full name"
                value={form.fullName}
                onChange={(event) => updateField("fullName", event.target.value)}
                fullWidth
                required
              />
              <TextField
                label="Email"
                type="email"
                value={form.email}
                onChange={(event) => updateField("email", event.target.value)}
                fullWidth
                required
              />
            </Stack>

            <TextField
              label="Phone"
              value={form.phone}
              onChange={(event) => updateField("phone", event.target.value)}
              fullWidth
            />

            <SmsConsentCheckbox
              checked={smsConsent}
              onChange={setSmsConsent}
              error={error?.toLowerCase().includes("sms consent") ? error : ""}
            />

            <FormControl component="fieldset" variant="standard">
              <FormLabel component="legend">Preferred roles</FormLabel>
              <FormGroup
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 0.5,
                  mt: 1,
                }}
              >
                {JOB_ALERT_ROLE_OPTIONS.map((option) => (
                  <FormControlLabel
                    key={option}
                    control={
                      <Checkbox
                        checked={form.preferredRoles.includes(option)}
                        onChange={() =>
                          updateField(
                            "preferredRoles",
                            toggleArrayValue(form.preferredRoles, option)
                          )
                        }
                      />
                    }
                    label={option}
                  />
                ))}
              </FormGroup>
              <TextField
                sx={{ mt: 1.25 }}
                label="Other role (optional)"
                placeholder="Add another role if needed"
                value={form.otherPreferredRole}
                onChange={(event) => updateField("otherPreferredRole", event.target.value)}
                fullWidth
              />
            </FormControl>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
                gap: 2,
              }}
            >
              <TextField
                select
                label="Preferred Country"
                value={asSelectValue(form.preferredCountryId)}
                onChange={(event) =>
                  setForm((prev) => ({
                    ...prev,
                    preferredCountryId: event.target.value ? Number(event.target.value) : null,
                    preferredStateId: null,
                    preferredCityId: null,
                  }))
                }
                fullWidth
                required
              >
                <MenuItem value="">
                  <em>Select a country</em>
                </MenuItem>
                {countries.map((country) => (
                  <MenuItem key={String(country.id)} value={String(country.id)}>
                    {country.name}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                label="Preferred State"
                value={asSelectValue(form.preferredStateId)}
                onChange={(event) =>
                  setForm((prev) => ({
                    ...prev,
                    preferredStateId: event.target.value ? Number(event.target.value) : null,
                    preferredCityId: null,
                  }))
                }
                fullWidth
                required
                disabled={!preferredStates.length}
                helperText={preferredStates.length ? " " : "Select a country first."}
              >
                <MenuItem value="">
                  <em>{preferredStates.length ? "Select a state" : "Select a country first"}</em>
                </MenuItem>
                {preferredStates.map((state) => (
                  <MenuItem key={String(state.id)} value={String(state.id)}>
                    {state.name}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                label="Preferred City"
                value={asSelectValue(form.preferredCityId)}
                onChange={(event) =>
                  updateField(
                    "preferredCityId",
                    event.target.value ? Number(event.target.value) : null
                  )
                }
                fullWidth
                required
                disabled={!preferredCities.length}
                helperText={preferredCities.length ? " " : "Select a state first."}
              >
                <MenuItem value="">
                  <em>{preferredCities.length ? "Select a city" : "Select a state first"}</em>
                </MenuItem>
                {preferredCities.map((city) => (
                  <MenuItem key={String(city.id)} value={String(city.id)}>
                    {city.name}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            <FormControl component="fieldset" variant="standard">
              <FormLabel component="legend">Employment type</FormLabel>
              <FormGroup
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 0.5,
                  mt: 1,
                }}
              >
                {JOB_ALERT_EMPLOYMENT_TYPE_OPTIONS.map((option) => (
                  <FormControlLabel
                    key={option}
                    control={
                      <Checkbox
                        checked={form.employmentTypes.includes(option)}
                        onChange={() =>
                          updateField(
                            "employmentTypes",
                            toggleArrayValue(form.employmentTypes, option)
                          )
                        }
                      />
                    }
                    label={option}
                  />
                ))}
              </FormGroup>
              <TextField
                sx={{ mt: 1.25 }}
                label="Other employment type (optional)"
                placeholder="Add another employment type if needed"
                value={form.otherEmploymentType}
                onChange={(event) => updateField("otherEmploymentType", event.target.value)}
                fullWidth
              />
              <FormHelperText>
                Select as many as you want. We will use these preferences when we review
                matching openings.
              </FormHelperText>
            </FormControl>

            <TextField
              label="Anything else we should know?"
              multiline
              minRows={3}
              value={form.notes}
              onChange={(event) => updateField("notes", event.target.value)}
            />

            {error ? <Alert severity="error">{error}</Alert> : null}
            {success ? (
              <Alert severity="success">
                {success} You can browse and apply now at{" "}
                <Typography
                  component={RouterLink}
                  to="/care/jobs"
                  sx={{ fontWeight: 700, color: "inherit", textDecoration: "underline" }}
                >
                  /care/jobs
                </Typography>
                .
              </Alert>
            ) : null}

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
              <Button type="submit" variant="contained" disabled={submitting}>
                {submitting ? "Saving..." : "Get Free Job Alerts"}
              </Button>
              <Button component={RouterLink} to="/care/jobs" variant="outlined">
                Browse Open Jobs
              </Button>
              <Button component={RouterLink} to="/care/jobs/pricing" variant="outlined">
                See Pricing
              </Button>
              <Button component={RouterLink} to="/care/myjobs" variant="text">
                My Jobs & Profile
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Stack>
    </Paper>
  );
}
